import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { aviatorGameId, aviatrixGameId, resetGameMode, resetGameUrl, selectUserCurrency, setGameMode, setGameUrl, toTypedUpperCase, useCheckRealms, useGame, useGameUrl } from '@mwl/core-lib';
import { NoSSR } from '@mwl/ui';
import { aviatrixCurrency, aviatrixRealms, emptyUrl, routes } from '@/constants';
import { useTypedDispatch, useTypedSelector } from '@/hooks';
import { toggleAviatrixPopup } from '@/store/features/ui/slice';
import { BottomSection } from './components/BottomSection/BottomSection';
import { CasinoGameModals } from './components/CasinoGameModals/CasinoGameModals';
import { GameLayout } from './components/GameLayout/GameLayout';
import { GameSection } from './components/GameSection/GameSection';
import { Header } from './components/Header/Header';
import { PopupAviatrix } from './components/PopupAvaitrix/PopupAviatrix';
import { PopupBalanceBonus } from './components/PopupBalanceBonus/BalanceBonusPopup';
import { PopupBalanceConversion } from './components/PopupBalanceConversion/PopupBalanceConversion';
import { PopupBalanceNotEnoughMoney } from './components/PopupBalanceNotEnoughMoney/PopupBalanceNotEnoughMoney';
import { SideSection } from './components/SideSection/SideSection';
import { FullscreenProvider } from './CasinoGamePage.context';
import type { CasinoGamePageProps } from './CasinoGamePage.types';
export const CasinoGamePage: FC<CasinoGamePageProps> = ({
  gameId,
  mode,
  isCountryAvailable
}) => {
  const dispatch = useTypedDispatch();
  const router = useRouter();
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const {
    data: game
  } = useGame({
    id: gameId
  });
  const isNeedRedirectFromAviator = useCheckRealms(aviatrixRealms);
  const userCurrency = useTypedSelector(selectUserCurrency);
  const {
    isLoading: urlIsLoading,
    gameUrl,
    error: urlError,
    refreshRequest,
    gameMode,
    handleStartLoading,
    isNeedConversion,
    isRealModeError,
    isBonusModeError,
    isInitialLoaded
  } = useGameUrl({
    mode,
    id: gameId,
    game
  });
  const isRedirectToAviatrixNeeded = aviatorGameId === gameId && (isNeedRedirectFromAviator || aviatrixCurrency === userCurrency || !isCountryAvailable);
  const balanceNotEnoughMoneyIsShow = !!(isRealModeError && isBonusModeError && !isInitialLoaded);
  useEffect(() => {
    if (!urlIsLoading && isRedirectToAviatrixNeeded) {
      router.replace({
        pathname: routes.casino.game,
        query: {
          gameId: aviatrixGameId,
          mode: !balanceNotEnoughMoneyIsShow && mode === 'REAL' ? 'real' : 'demo'
        }
      });
      dispatch(toggleAviatrixPopup(true));
    }
  }, [urlIsLoading, gameId, isRedirectToAviatrixNeeded, router, mode, dispatch, balanceNotEnoughMoneyIsShow]);
  useEffect(() => {
    if (gameUrl && gameUrl !== emptyUrl) {
      dispatch(setGameUrl(gameUrl));
    } else {
      dispatch(resetGameUrl());
    }
  }, [gameUrl, dispatch]);
  useEffect(() => {
    if (gameMode) {
      dispatch(setGameMode(gameMode));
    } else {
      dispatch(resetGameMode());
    }
  }, [gameMode, dispatch]);
  useEffect(() => {
    return () => {
      dispatch(resetGameUrl());
      dispatch(resetGameMode());
    };
  }, [dispatch]);
  useEffect(() => {
    window.scroll(0, 0);
  }, []);
  return <FullscreenProvider url={gameUrl} data-sentry-element="FullscreenProvider" data-sentry-component="CasinoGamePage" data-sentry-source-file="CasinoGamePage.tsx">
      <GameLayout headerSection={<Header mode={gameMode} game={game} />} gameSection={<GameSection gameId={String(gameId)} mode={mode} isLoading={urlIsLoading} />} sideSection={<SideSection />} bottomSection={<BottomSection />} gameContainerRef={gameContainerRef} data-sentry-element="GameLayout" data-sentry-source-file="CasinoGamePage.tsx" />
      <CasinoGameModals urlError={urlError} mode={toTypedUpperCase(mode)} game={game} refreshRequest={refreshRequest} data-sentry-element="CasinoGameModals" data-sentry-source-file="CasinoGamePage.tsx" />
      <PopupBalanceBonus defaultIsShow={gameMode === 'BONUS' && !isInitialLoaded} onStartLoading={handleStartLoading} data-sentry-element="PopupBalanceBonus" data-sentry-source-file="CasinoGamePage.tsx" />
      <PopupBalanceConversion defaultIsShow={!!(gameMode === 'REAL' && isNeedConversion)} onStartLoading={handleStartLoading} game={game} data-sentry-element="PopupBalanceConversion" data-sentry-source-file="CasinoGamePage.tsx" />
      <PopupBalanceNotEnoughMoney defaultIsShow={!isRedirectToAviatrixNeeded && balanceNotEnoughMoneyIsShow} game={game} data-sentry-element="PopupBalanceNotEnoughMoney" data-sentry-source-file="CasinoGamePage.tsx" />
      <NoSSR data-sentry-element="NoSSR" data-sentry-source-file="CasinoGamePage.tsx">
        <PopupAviatrix portalContainer={gameContainerRef.current} gameId={gameId} data-sentry-element="PopupAviatrix" data-sentry-source-file="CasinoGamePage.tsx" />
      </NoSSR>
    </FullscreenProvider>;
};